import React from "react";
import { Users, Heart, Target, Award } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

type OrganizerEntry = {
  nome: string;
  ruolo: { ro: string; it: string };
  descriere: { ro: string; it: string };
  foto: string;
};

const organizers: OrganizerEntry[] = [
  {
    nome: "Milorad Tiberiu Martinaș",
    ruolo: {
      ro: "Reprezentant legal",
      it: "Rappresentante legale",
    },
    descriere: {
      ro: "Iubirea profundă pentru rădăcinile sale și dorința sinceră de a aduce zâmbete și bucurie în inimile românilor din Italia l-au determinat să construiască totul cu responsabilitate și profesionalism. Este omul care face lucrurile „ca la carte”, punând mereu pe primul loc seriozitatea, corectitudinea și respectul față de comunitate.",
      it: "Il suo profondo amore per le proprie radici e il sincero desiderio di portare sorrisi e gioia nei cuori dei romeni in Italia lo hanno spinto a costruire tutto con responsabilità e professionalità. È la persona che fa le cose “a regola d’arte”, mettendo sempre al primo posto serietà, correttezza e rispetto verso la comunità.",
    },
    foto: "/tibe.jpg",
  },
  {
    nome: "Roxana Paula Bitica",
    ruolo: {
      ro: "Responsabil relații artiști & comunicare",
      it: "Responsabile relazioni con gli artisti & comunicazione",
    },
    descriere: {
      ro: "Cu ani de experiență în domeniul vânzărilor și o pasiune autentică pentru frumos, Roxana este puntea de legătură dintre asociație și artiști. Abilitățile sale de comunicare și energia pozitivă creează conexiuni armonioase și colaborări de succes.",
      it: "Con anni di esperienza nel settore delle vendite e una passione autentica per la bellezza, Roxana è il ponte tra l’associazione e gli artisti. Le sue capacità comunicative e la sua energia positiva creano connessioni armoniose e collaborazioni di successo.",
    },
    foto: "/roxi.jpg",
  },
  {
    nome: "Răzvan Teodor Damian",
    ruolo: {
      ro: "Coordonator organizare & administrativ",
      it: "Coordinatore organizzativo & amministrativo",
    },
    descriere: {
      ro: "Cu experiență solidă în organizarea evenimentelor și în îndrumarea oamenilor către drumul lor potrivit, Damian este omul de bază al echipei. Se ocupă de partea birocratică și administrativă, asigurând structura și stabilitatea fiecărui proiect.",
      it: "Con una solida esperienza nell’organizzazione di eventi e nell’accompagnare le persone verso il loro percorso più adatto, Damian è un punto di riferimento fondamentale per il team. Si occupa della parte burocratica e amministrativa, garantendo struttura e stabilità a ogni progetto.",
    },
    foto: "/razvan.jpg",
  },
  {
    nome: "Indira Andreea Brindau",
    ruolo: {
      ro: "Director creativ & marketing",
      it: "Direttore creativo & marketing",
    },
    descriere: {
      ro: "Cu experiență în lumea spectacolului și în marketing, Indira este mintea creativă din spatele proiectelor. Ea transformă ideile în concepte memorabile și aduce frumusețe, coerență și impact în tot ceea ce ține de imagine și comunicare vizuală.",
      it: "Con esperienza nel mondo dello spettacolo e nel marketing, Indira è la mente creativa dietro ai progetti. Trasforma le idee in concetti memorabili e porta bellezza, coerenza e impatto in tutto ciò che riguarda l’immagine e la comunicazione visiva.",
    },
    foto: "/indira.jpg",
  },
  {
    nome: "Cosmin Daniel Bitica",
    ruolo: {
      ro: "Coordonator logistic & relații furnizori",
      it: "Coordinatore logistico & relazioni con i fornitori",
    },
    descriere: {
      ro: "Cu experiență în organizarea logistică, Cosmin este puntea dintre asociație și furnizori. Se ocupă de coordonarea logisticii și gestionează relațiile cu partenerii, asigurând organizare, eficiență și buna desfășurare a fiecărui eveniment.",
      it: "Con esperienza nell’organizzazione logistica, Cosmin è il ponte tra l’associazione e i fornitori. Si occupa del coordinamento logistico e della gestione dei rapporti con i partner, garantendo organizzazione, efficienza e il corretto svolgimento di ogni evento.",
    },
    foto: "/cus.jpg",
  },
];

const About: React.FC = () => {
  const { t, language } = useLanguage();
  const langKey = language === "ro" ? "ro" : "it";

  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: t.about.passion,
      description: t.about.passionDesc,
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t.about.community,
      description: t.about.communityDesc,
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: t.about.authenticity,
      description: t.about.authenticityDesc,
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: t.about.excellence,
      description: t.about.excellenceDesc,
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-romanian-blue to-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-romanian-yellow rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-romanian-red rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-romanian-yellow font-semibold text-sm uppercase tracking-wider">
              {t.about.aboutUs}
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              {t.about.title}
            </h1>
            <p className="text-white/90 text-lg md:text-xl leading-relaxed">
              {t.about.description}
            </p>
          </div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none">
            <path
              d="M0 120L48 110C96 100 192 80 288 70C384 60 480 60 576 65C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H0Z"
              fill="#fafafa"
            />
          </svg>
        </div>
      </section>

      {/* Story Section */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-romanian-red font-semibold text-sm uppercase tracking-wider">
                {t.about.ourMission}
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-romanian-blue mt-2 mb-6">
                {t.about.missionTitle}
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>{t.about.missionText1}</p>
                <p>{t.about.missionText2}</p>
                <p>{t.about.missionText3}</p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/festival 2.png"
                  alt="Festival crowd"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-romanian-yellow rounded-xl flex items-center justify-center shadow-lg">
                <div className="text-center">
                  <span className="block text-3xl font-bold text-romanian-blue">
                    1
                  </span>
                  <span className="text-sm text-romanian-blue">
                    {t.about.editions}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-romanian-red font-semibold text-sm uppercase tracking-wider">
              {t.about.ourValues}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-romanian-blue mt-2 mb-4">
              {t.about.whatGuidesUs}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-romanian-blue via-romanian-yellow to-romanian-red mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-romanian-blue to-primary-600 rounded-xl flex items-center justify-center text-white mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="font-semibold text-xl text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-romanian-red font-semibold text-sm uppercase tracking-wider">
              {t.about.ourTeam}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-romanian-blue mt-2 mb-4">
              {t.about.organizers}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-romanian-blue via-romanian-yellow to-romanian-red mx-auto mb-4" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t.about.teamDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {organizers.map((organizer, index) => (
              <div key={index} className="card overflow-hidden group">
                <div className="relative h-[34rem] md:h-[40rem] overflow-hidden">
                  <img
                    src={organizer.foto}
                    alt={organizer.nome}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 space-y-2">
                  <div>
                    <h3 className="font-semibold text-xl text-gray-900">
                      {organizer.nome}
                    </h3>
                    <p className="text-romanian-blue text-sm font-medium">
                      {organizer.ruolo[langKey]}
                    </p>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {organizer.descriere[langKey]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
